/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { UTC, utc, DateRange, isNullOrUndefined, isNullOrUndefinedOrEmpty, TimeSpan, TimestampRange } from 'rewire-common';
import { observable, freeze, computed, observe, root } from 'rewire-core';

import { createGrid, createColumn, IGrid, IColumn, IRowData, ICell, IGridOptions } from 'rewire-grid';
import { DeepPartial, IShift, ITimecard, IEmployee, ISchedule, IScheduleRuleContext, IReason, IOccupation } from '../DataTypes';
import { roundTo } from '../utility/DataConverters';
import DataQueries from '../graphql/Queries';
import { formatTime, localTimeZoneUTCNow } from '../utility/DateUtils';
import { getCustomFormDatePickerFn } from '../ui/SchedulingToolCustomFormDatePicker';
import DateRangeNavModel, { DateDuration, durationToDays } from '../ui/DateRangeNavModel';
import { SchedulingEmployeeCell, getSchedulingShiftCellRenderer } from './SchedulingCandidateEmployeeGrid';
import infoPanelShiftViewModel from './InfoPanelShiftViewModel';
import scheduleModel from './ScheduleModel';
import mainPageModel from '../main-navigation/MainPageModel';

export type AvailabilityStatus = 'Available' | 'NotAvailable' | 'WorkingNonIntersectingTimes';

export interface IShiftEntry {
  details: ITimecard[];
  effective?: TimestampRange;
  units: number;
  reason?: IReason;
  text: IShiftEntryText;
}

export interface IShiftEntryText {
  effective: string;
  units: string;
  reason: string;
  occupation: string;
}

export interface IShiftEntryData {
  effective?: TimestampRange;
  units: number;
  reason?: IReason;
  occupation?: IOccupation;
}

export class SchedulingShiftCellData {
  earnedDate: UTC;
  details?: ITimecard[];
  shiftEntriesText: IShiftEntryText[];
  availability?: AvailabilityStatus;

  constructor(shift: IShift, availability?: AvailabilityStatus) {
    ({ earnedDate: this.earnedDate, details: this.details } = shift);
    this.shiftEntriesText = this.getShiftEntriesText();
    this.availability = availability;
  }

  toString(): string {
    if (!this.shiftEntriesText.length) {
      return '';
    }
    const firstShiftEntry = this.shiftEntriesText[0];
    return `${firstShiftEntry.effective ? firstShiftEntry.effective + '\n' : ''}(${firstShiftEntry.units} ${firstShiftEntry.reason})\n${firstShiftEntry.occupation}${this.shiftEntriesText.length > 1 ? '\n' + (this.shiftEntriesText.length - 1) + ' More' : ''}`;
  }

  getShiftEntriesText(): IShiftEntryText[] {
    if (!this.details?.length) {
      return [];
    }

    const details = this.details.slice();
    const breaksToHandle = details.filter((d) => d.reason?.category === 'break');
    const groupedDetails = {} as { [s: string]: ITimecard[] };
    const nonAbsenceGroupedDetails = {} as { [s: string]: ITimecard[] };
    const absenceGroupedDetails = {} as { [s: string]: ITimecard[] };
    const breaksFromDetailsToHandle = [] as ITimecard[];
    details.forEach((detail: ITimecard) => {
      if (detail.reason?.category === 'break') {
        breaksFromDetailsToHandle.push(detail);
        return;
      }
      const isAbsence = detail.reason?.category === 'absence';
      const reasonKey = detail.reason?.id || 'noReason';
      const key = `${reasonKey}`;
      if (!groupedDetails[key]) {
        groupedDetails[key] = [];
        if (isAbsence) {
          absenceGroupedDetails[key] = groupedDetails[key];
        } else {
          nonAbsenceGroupedDetails[key] = groupedDetails[key];
        }
      }
      groupedDetails[key].push(detail);
    });

    const getBreakPlacementIndex = (details: ITimecard[], breakDetail: ITimecard): number | undefined => {
      let placementIndex: number | undefined;
      for (let j = 0; j < details.length; j++) {
        const detail = details[j];
        if ((!detail.effective && breakDetail.effective) || (detail.effective && !breakDetail.effective)) {
          continue;
        }
        if (!detail.effective && !breakDetail.effective) {
          if (detail.reason?.category === 'absence') {
          } else {
            placementIndex = j + 1;
            break;
          }
        } else if (detail.effective!.start.equals(breakDetail.effective!.end)) {
          placementIndex = j;
          break;
        } else if (detail.effective!.end.equals(breakDetail.effective!.start)) {
          placementIndex = j + 1;
          break;
        }
      }
      return placementIndex;
    };

    if (breaksFromDetailsToHandle.length) {
      // add breaks to groupedDetails if contiguous
      breaksFromDetailsToHandle.forEach((breakDetail: ITimecard) => {
        let reasonKey = breakDetail.reason?.id || 'noReason';
        let placementIndex: number | undefined;
        const nonAbsenceGroupedDetailsKeys = Object.keys(nonAbsenceGroupedDetails);
        for (let i = 0; i < nonAbsenceGroupedDetailsKeys.length; i++) {
          const nonAbsenceGroupedDetailsKey = nonAbsenceGroupedDetailsKeys[i];
          const detailsForKey = nonAbsenceGroupedDetails[nonAbsenceGroupedDetailsKey];
          placementIndex = getBreakPlacementIndex(detailsForKey, breakDetail);
          if (!isNullOrUndefined(placementIndex)) {
            reasonKey = nonAbsenceGroupedDetailsKey;
            break;
          }
        }
        const key = `${reasonKey}`;
        if (!groupedDetails[key]) {
          groupedDetails[key] = [];
          nonAbsenceGroupedDetails[key] = groupedDetails[key];
        }
        if (!isNullOrUndefined(placementIndex)) {
          nonAbsenceGroupedDetails[key].splice(placementIndex, 0, breakDetail);
        } else {
          nonAbsenceGroupedDetails[key].push(breakDetail);
        }
      });
    }

    if (breaksToHandle.length) {
      // add breaks to every absence grouped details if contiguous
      Object.keys(absenceGroupedDetails).forEach((gdKey) => {
        let placementIndex: number | undefined;
        const details = groupedDetails[gdKey];
        for (let i = 0; i < breaksToHandle.length; i++) {
          const breakDetail = breaksToHandle[i];
          placementIndex = getBreakPlacementIndex(details, breakDetail);

          if (!isNullOrUndefined(placementIndex)) {
            details.splice(placementIndex, 0, breakDetail);
          }
        }
      });
    }

    const groupedShiftEntries = Object.keys(groupedDetails).map((key: string) => {
      const detailsGroup = groupedDetails[key];
      const isAbsenceGroup = Object.keys(absenceGroupedDetails).some((agdKey) => agdKey === key);
      const detailsContiguousGroups = [] as ITimecard[][];
      const absenceDetailsContiguousGroups = [] as ITimecard[][];
      let detailsContiguousGroup = [] as ITimecard[];
      detailsGroup.forEach((detail: ITimecard) => {
        const groupLength = detailsContiguousGroup.length;
        const detailStart = detail.effective?.start;
        const lastGroupDetailEndDate = groupLength && detailsContiguousGroup[groupLength - 1].effective?.end;
        if (
          groupLength &&
          ((!lastGroupDetailEndDate && detailStart) ||
            (lastGroupDetailEndDate && !detailStart) ||
            (lastGroupDetailEndDate && detailStart && !lastGroupDetailEndDate.equals(detailStart)))
        ) {
          detailsContiguousGroups.push(detailsContiguousGroup);
          if (isAbsenceGroup) {
            // store groups that are absences so that the break details can be removed after calculating timecard entries.
            absenceDetailsContiguousGroups.push(detailsContiguousGroup);
          }
          detailsContiguousGroup = [] as ITimecard[];
        }
        detailsContiguousGroup.push(detail);
      });

      if (detailsContiguousGroup.length) {
        detailsContiguousGroups.push(detailsContiguousGroup);
        if (isAbsenceGroup) {
          // store groups that are absences so that the break details can be removed after calculating timecard entries.
          absenceDetailsContiguousGroups.push(detailsContiguousGroup);
        }
      }

      const shiftEntryData = detailsContiguousGroups.map((group: ITimecard[]) => {
        let start: UTC | undefined;
        let end: UTC | undefined;
        let units = 0;
        const occupation = group[0].occupation;
        const reason = group.reduce((prev: ITimecard, curr: ITimecard) =>
          !prev.reason || prev.reason.category === 'break' ? curr : prev,
        ).reason;

        group.forEach((detail: ITimecard) => {
          const detailStart = detail.effective?.start;
          const detailEnd = detail.effective?.end;
          const detailUnits = detail.units;
          const detailReason = detail.reason;
          if (detailStart && (start === undefined || detailStart.date < start.date)) {
            start = detailStart;
          }
          if (detailEnd && (end === undefined || detailEnd.date > end.date)) {
            end = detailEnd;
          }
          if (detailUnits && (!detailReason || detailReason.category !== 'break')) {
            units += detailUnits;
          }
        });

        const effective =
          start && end && !Number.isNaN(start.utc) && !Number.isNaN(end.utc) ? new TimestampRange(start, end) : undefined;

        const shiftEntryText: IShiftEntryText = {
          occupation: occupation?.name || '',
          effective: (effective?.start && effective?.end && `${formatTime(effective.start)}-${formatTime(effective.end)}`) || '',
          units: String(roundTo(units, 2)),
          reason: reason?.name || '',
        };

        const shiftEntry: IShiftEntry = {
          details: group,
          effective: effective,
          units: units,
          reason: reason,
          text: shiftEntryText,
        };
        return shiftEntry;
      });

      // remove break details from absence time entries.
      absenceDetailsContiguousGroups.forEach((details: ITimecard[]) => {
        for (let i = details.length - 1; i >= 0; i--) {
          const detail = details[i];
          if (detail.reason?.category === 'break') {
            details.splice(i, 1);
          }
        }
      });

      return shiftEntryData;
    });

    const flattenedGroupedShiftEntries = groupedShiftEntries.flat();
    this.sortShiftEntries(flattenedGroupedShiftEntries);
    return flattenedGroupedShiftEntries.map((e) => e.text);
  }

  sortShiftEntries(shiftEntries: IShiftEntry[]) {
    shiftEntries.sort((shiftEntry1: IShiftEntry, shiftEntry2: IShiftEntry) => {
      const isAbs1 = shiftEntry1.reason?.category === 'absence';
      const isAbs2 = shiftEntry2.reason?.category === 'absence';
      let sortResult = !isAbs1 && isAbs2 ? -1 : isAbs1 && !isAbs2 ? 1 : 0;

      if (sortResult === 0) {
        const startTime1 = shiftEntry1.effective?.start?.date;
        const startTime2 = shiftEntry2.effective?.start?.date;
        const hasStartTime1 = !isNullOrUndefined(startTime1);
        const hasStartTime2 = !isNullOrUndefined(startTime2);
        sortResult =
          !hasStartTime1 && hasStartTime2
            ? -1
            : hasStartTime1 && !hasStartTime2
              ? 1
              : !hasStartTime1 && !hasStartTime2
                ? 0
                : startTime1! < startTime2!
                  ? -1
                  : startTime1! > startTime2!
                    ? 1
                    : 0;
      }
      if (sortResult === 0) {
        const endTime1 = shiftEntry1.effective?.end?.date;
        const endTime2 = shiftEntry2.effective?.end?.date;
        const hasEndTime1 = !isNullOrUndefined(endTime1);
        const hasEndTime2 = !isNullOrUndefined(endTime2);
        sortResult =
          !hasEndTime1 && hasEndTime2
            ? -1
            : hasEndTime1 && !hasEndTime2
              ? 1
              : !hasEndTime1 && !hasEndTime2
                ? 0
                : endTime1! < endTime2!
                  ? -1
                  : endTime1! > endTime2!
                    ? 1
                    : 0;
      }
      if (sortResult === 0) {
        const reason1 = shiftEntry1.reason;
        const reason2 = shiftEntry2.reason;
        const hasReason1 = !isNullOrUndefined(reason1);
        const hasReason2 = !isNullOrUndefined(reason2);
        sortResult =
          !hasReason1 && hasReason2
            ? -1
            : hasReason1 && !hasReason2
              ? 1
              : !hasReason1 && !hasReason2
                ? 0
                : reason1!.id.localeCompare(reason2!.id);
      }
      if (sortResult === 0) {
        const units1 = shiftEntry1.units;
        const units2 = shiftEntry2.units;
        sortResult = units1! < units2! ? -1 : units1! > units2! ? 1 : 0;
      }
      if (sortResult === 0) {
        const timecardId1 = shiftEntry1.details[0]?.id ?? '';
        const timecardId2 = shiftEntry2.details[0]?.id ?? '';
        sortResult = timecardId1!.localeCompare(timecardId2!);
      }
      return sortResult;
    });
  }
}

interface ISchedulingCandidateEmployeeGridModelState {
  dateRangeDuration: DateDuration;
  schedulingGrid: IGrid;
  employeeSchedules: any[];
  schedulingShiftCell?: ICell;
  scheduleRuleContext?: IScheduleRuleContext;
  scheduleRuleQuery?: string;
  isLoading: boolean;
  isGridLoading: boolean;
}

export class SchedulingCandidateEmployeeGridModel {
  state: ISchedulingCandidateEmployeeGridModelState;
  dateRangeNavModel: DateRangeNavModel;
  getCenteredDateRange: () => DateRange;

  constructor(
    protected _onEarnedDateChange?: (date?: UTC) => void,
    protected _showAddScheduleRuleDialogFn?: () => void,
  ) {
    this.state = observable({
      dateRangeDuration: '2 Weeks',
      schedulingGrid: {} as IGrid,
      employeeSchedules: [],
      schedulingShiftCell: undefined,
      scheduleRuleContext: undefined,
      scheduleRuleQuery: undefined,
      isLoading: false,
      isGridLoading: false,
    });

    this.dateRangeNavModel = new DateRangeNavModel(
      this.onDateNavStartDateChange,
      this.onDateNavEndDateChange,
      this.onDateNavDateDurationChange,
      getCustomFormDatePickerFn(() => this.earnedDate),
    );

    const centeredDateRangeComputation = () => {
      const dateDurationDays = durationToDays[this.dateRangeDuration];
      const earnedDate = this.earnedDate;
      const dateOffset = Math.floor(dateDurationDays / 2);
      const startDateOffset = -dateOffset;
      const endDateOffset = dateDurationDays - dateOffset - 1;
      const startDate = earnedDate.add(startDateOffset);
      const endDate = earnedDate.add(endDateOffset);
      return new DateRange(startDate, endDate);
    };

    root(() => {
      const earnedDateChanged = observe(() => {
        this.earnedDate;
        this.dateRangeDuration;
      });
      this.getCenteredDateRange = computed(earnedDateChanged, centeredDateRangeComputation, centeredDateRangeComputation());
    });
  }

  reset() {
    freeze(() => {
      this.state.schedulingGrid = {} as IGrid;
      this.state.employeeSchedules = [];
      this.state.schedulingShiftCell = undefined;
      this.state.scheduleRuleContext = undefined;
      this.state.scheduleRuleQuery = undefined;
    });
    this.dateRangeNavModel.reset();
  }

  async load(
    scheduleRuleContext?: IScheduleRuleContext,
    scheduleRuleQuery?: string,
    earnedDate?: UTC,
    initialLoad: boolean = false,
    disableDate: boolean = false,
  ) {
    this.state.isLoading = true;
    await (this.state.scheduleRuleContext = scheduleRuleContext);
    await (this.state.scheduleRuleQuery = scheduleRuleQuery);
    this.setDateRange(earnedDate);
    this.dateRangeNavModel.disableDate = disableDate;
    await this.loadGrid();
    this.state.isLoading = false;
  }

  loadGrid = async () => {
    this.state.isGridLoading = true;
    this.state.employeeSchedules = [];
    this.state.schedulingGrid = {} as IGrid;
    if (!this.scheduleRuleQuery || !this.scheduleRuleContext) {
      this.state.isGridLoading = false;
      return;
    }

    this.state.employeeSchedules = await this.getEmployeeSchedules();
    this.state.schedulingGrid = this.createSchedulingGrid();
    if (this.schedulingGrid.rows.length > 0 && this.shiftEarnedDateColumnName) {
      // center grid scroll on shift earned date
      const cellToSelect = this.schedulingGrid.rows[0].cells[this.shiftEarnedDateColumnName];
      setTimeout(() => {
        this.schedulingGrid?.selectCells?.([cellToSelect]);
        this.schedulingGrid?.selectCells?.([]);
      }, 150);
    }
    this.state.isGridLoading = false;
  };

  getEmployeeSchedules = async (): Promise<DeepPartial<ISchedule>[]> => {
    if (isNullOrUndefinedOrEmpty(this.scheduleRuleQuery)) {
      return [];
    }
    const returnSchema = `
      id
      employee {
        id
        name
        phone {
          home
          mobile
          work
        }
      }
      schedule {
        earnedDate
        details {
          id
          effective
          units
          shift
          employee {
            id
            name
          }
          occupation {
            id
            name
          }
          reason {
            id
            name
            category
          }
        }
      }
    `;

    const result = await DataQueries.getSchedulingToolEmployeeSchedule(
      returnSchema,
      this.getCenteredDateRange(),
      this.scheduleRuleContext!,
      this.scheduleRuleQuery,
    );
    const employeeSchedules = result?.data;
    if (employeeSchedules && employeeSchedules.length) {
      employeeSchedules.forEach((schedule: ISchedule) => {
        schedule.schedule.forEach((shift: IShift) => {
          // filter out details with premium reason codes
          shift.details = shift.details?.filter(
            (detail: ITimecard) => !detail.reason || (detail.reason.category !== 'premium' && detail.reason.category !== 'other'),
          );
          if (!shift.details) return;
          shift.details && scheduleModel.sortShiftTimecardData(shift.details, true);
        });
      });
    }
    return employeeSchedules;
  };

  get startDate(): UTC {
    return this.getCenteredDateRange().start;
  }

  get startDateStartOfDay(): UTC {
    return this.getCenteredDateRange().start.clone().startOfDay();
  }

  get endDate(): UTC {
    return this.getCenteredDateRange().end;
  }

  get numOfDays(): number {
    return this.dateRangeNavModel.numOfDays!;
  }

  get dateRangeDuration(): DateDuration {
    return this.state.dateRangeDuration;
  }

  get scheduleRuleContext(): IScheduleRuleContext | undefined {
    return this.state.scheduleRuleContext;
  }

  get scheduleRuleQuery(): string | undefined {
    return this.state.scheduleRuleQuery;
  }

  get employeeSchedules(): any[] {
    return this.state.employeeSchedules;
  }

  get schedulingGrid(): IGrid {
    return this.state.schedulingGrid;
  }

  get earnedDate(): UTC {
    return this.dateRangeNavModel.startDate ?? UTC.now().startOfDay();
  }

  get viewOnly(): boolean {
    return mainPageModel.scheduleViewOnly;
  }

  get isLoading(): boolean {
    return this.state.isLoading || this.state.isGridLoading;
  }

  get isGridLoading(): boolean {
    return this.state.isGridLoading;
  }

  get showAddScheduleRuleDialogFn(): (() => void) | undefined {
    return this._showAddScheduleRuleDialogFn;
  }

  get selectedSchedulingEmployee(): IEmployee | undefined {
    const row = this.schedulingGrid.selectedRows?.[0];
    return row && scheduleModel.getEmployeeFromRow(row);
  }

  get hasSchedulingResults(): boolean {
    return !!this.employeeSchedules?.length;
  }

  get schedulingShiftCell(): ICell | undefined {
    return this.state.schedulingShiftCell;
  }

  get schedulingShiftCellDataPopupIsOpen(): boolean {
    return !!this.schedulingShiftCell;
  }

  get shiftEarnedDateColumnName(): string | undefined {
    const offset = this.earnedDate.subtract(this.startDate) + 1;
    const maxDays = this.numOfDays;
    return offset >= 1 && offset <= maxDays ? `shift${offset}` : undefined;
  }

  get selectedRows() {
    return this.schedulingGrid.selectedRows;
  }

  onDateNavStartDateChange = (startDate: UTC, _effective: DateRange) => {
    this._onEarnedDateChange?.(startDate);
    this.loadGrid();
  };

  onDateNavEndDateChange = (_endDate: UTC, _effective: DateRange) => {
    this.loadGrid();
  };

  onDateNavDateDurationChange = (dateDuration: DateDuration, _effective: DateRange) => {
    this.state.dateRangeDuration = dateDuration;
    this.loadGrid();
  };

  setDateRange(earnedDate?: UTC) {
    if (!earnedDate) return;
    const dateDurationDays = durationToDays[this.dateRangeDuration];
    const startDate = utc(earnedDate);
    const endDate = earnedDate.add(dateDurationDays);
    this.dateRangeNavModel.load({ effective: new DateRange(startDate, endDate) });
  }

  showSchedulingShiftCellDataPopup = (cell: ICell) => {
    this.state.schedulingShiftCell = cell;
  };

  hideSchedulingShiftCellDataPopup = () => {
    this.state.schedulingShiftCell = undefined;
  };

  toDisplayDateString(utc?: UTC): string {
    return infoPanelShiftViewModel.toDisplayDateString(utc);
  }

  toDisplaySchedulingGridDateString(utc?: UTC): string {
    if (!utc) return '';
    const d = new Date(utc.utc);
    const dayShort = d.toLocaleDateString('en-us', { weekday: 'short', timeZone: 'UTC' });
    const dayNum = d.getUTCDate();
    return `${dayNum} ${dayShort}`;
  }

  calculateAvailiability(details?: ITimecard[]): AvailabilityStatus {
    if (!this.scheduleRuleContext || !details?.length) {
      return 'Available';
    } else {
      for (const detail of details) {
        if (
          detail.reason?.category === 'absence' ||
          (detail.effective && this.scheduleRuleContext.effective?.intersects(detail.effective!))
        ) {
          return 'NotAvailable';
        }
      }

      return 'WorkingNonIntersectingTimes';
    }
  }

  formatCellTime(value: number): string {
    if (isNullOrUndefined(value)) {
      return value;
    }
    const date = localTimeZoneUTCNow().startOfDay().add(value, TimeSpan.hours, 4);
    return formatTime(date);
  }

  getShiftColumnEarnedDateByPosition(columnPosition: number): UTC {
    return this.startDateStartOfDay.add(columnPosition - 1);
  }

  createSchedulingGrid() {
    const cols: IColumn[] = observable([]);
    const nameObjMap = (value: any) => value?.name ?? '';
    const nameObjCompare = (x: any, y: any) => nameObjMap(x).localeCompare(nameObjMap(y));
    const employeeWidth = '95px';
    const dateWidth = '77px';

    cols.push(
      createColumn('employee', 'Employee', {
        width: employeeWidth,
        canSort: false,
        visible: true,
        type: 'none',
        fixed: true,
        map: nameObjMap,
        compare: nameObjCompare,
        renderer: SchedulingEmployeeCell,
      }),
    );
    for (let col = 1; col <= this.numOfDays; col++) {
      cols.push(
        createColumn('shift' + col, this.toDisplaySchedulingGridDateString(this.startDate.add(col - 1)), {
          type: 'none',
          width: dateWidth,
          align: 'left',
          canSort: false,
          verticalAlign: 'top',
          renderer: getSchedulingShiftCellRenderer(this),
        }),
      );
    }

    const rows: IRowData[] = [];

    for (let row = 0; row < this.employeeSchedules.length; row++) {
      const currRow = this.employeeSchedules[row];
      let scheduleIdx = 0;
      const r: IRowData = { id: `${row}`, data: {} };
      for (let column = 0; column < cols.length; column++) {
        const fieldName: string = cols[column].name;
        let v: any;
        if (fieldName.includes('shift')) {
          const columnDate = this.getShiftColumnEarnedDateByPosition(column);
          let shift = currRow.schedule?.[scheduleIdx] as IShift | undefined;
          shift = shift && columnDate.equals(shift.earnedDate) ? shift : undefined;
          const availability = columnDate.equals(this.earnedDate) ? this.calculateAvailiability(shift?.details) : undefined;
          v = new SchedulingShiftCellData(shift ?? { earnedDate: columnDate, details: [] }, availability);
          if (shift) {
            scheduleIdx++;
          }
        } else {
          v = this.employeeSchedules[row][fieldName];
        }
        r.data![fieldName] = v;
      }
      rows.push(r);
    }

    const gridOptions: IGridOptions = {
      headerRowHeight: 24,
      readOnly: true,
      rowKeybindPermissions: {
        insertRow: false,
        duplicateRow: false,
        deleteRow: false,
      },
      clearSelectionOnBlur: false,
    };

    const grid: IGrid = createGrid(rows, cols, gridOptions);
    return grid;
  }
}

export default SchedulingCandidateEmployeeGridModel;
